<template>
    <b-modal
        :visible="visible"
        title="인벤토리 비교 분석"
        size="xl"
        centered
        hide-footer
        body-class="compare-modal-body"
        @hidden="$emit('close')"
    >
        <div v-if="loading" class="compare-loading">
            <i class="fas fa-spinner fa-spin"></i>
            <span>비교 데이터 분석 중...</span>
        </div>
        <div v-else-if="result" class="compare-content">
            <div class="compare-top-row">
                <div class="scope-card main-card" :class="getSummaryChangeClass(result.summary.delta)">
                    <div class="scope-card-label">전체</div>
                    <div class="scope-delta-hero">
                        <i class="scope-delta-icon" :class="getDeltaIcon(result.summary.delta)"></i>
                        <span class="scope-delta-num">{{ formatDelta(result.summary.delta.absolute) }}</span>
                        <span class="scope-delta-unit">kgCO2e</span>
                        <span class="scope-delta-percent">{{ formatPercent(result.summary.delta.percentage) }}</span>
                    </div>
                    <div class="scope-card-context">
                        <div class="scope-context-side">
                            <span class="scope-context-boundary">{{ boundaryNames.org1 || "–" }}</span>
                            <span class="scope-context-values-row">
                                <span class="scope-context-val">{{
                                    formatNum(result.summary.inventory1.totalEmissions)
                                }}</span>
                                <span class="scope-context-unit">kgCO2e</span>
                            </span>
                        </div>
                        <div class="scope-context-divider"></div>
                        <div class="scope-context-side">
                            <span class="scope-context-boundary">{{ boundaryNames.org2 || "–" }}</span>
                            <span class="scope-context-values-row">
                                <span class="scope-context-val">{{
                                    formatNum(result.summary.inventory2.totalEmissions)
                                }}</span>
                                <span class="scope-context-unit">kgCO2e</span>
                            </span>
                        </div>
                    </div>
                </div>

                <div v-for="row in result.byScope" :key="row.scope" class="scope-card" :class="getScopeClass(row)">
                    <div class="scope-card-label">Scope {{ row.scope }}</div>
                    <div class="scope-delta-hero">
                        <i class="scope-delta-icon" :class="getDeltaIcon({ status: getChangeStatus(row.delta) })"></i>
                        <span class="scope-delta-num">{{ formatDelta(row.delta) }}</span>
                        <span class="scope-delta-unit">kgCO2e</span>
                        <span class="scope-delta-percent">{{ formatPercent(row.percentage) }}</span>
                    </div>
                    <div class="scope-card-context">
                        <div class="scope-context-side">
                            <span class="scope-context-boundary">{{ boundaryNames.org1 || "–" }}</span>
                            <span class="scope-context-values-row">
                                <span class="scope-context-val">{{ formatNum(row.inventory1) }}</span>
                                <span class="scope-context-unit">kgCO2e</span>
                            </span>
                        </div>
                        <div class="scope-context-divider"></div>
                        <div class="scope-context-side">
                            <span class="scope-context-boundary">{{ boundaryNames.org2 || "–" }}</span>
                            <span class="scope-context-values-row">
                                <span class="scope-context-val">{{ formatNum(row.inventory2) }}</span>
                                <span class="scope-context-unit">kgCO2e</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="detail-sections">
                <div v-if="result.byFacility && result.byFacility.length" class="detail-section">
                    <h4 class="detail-heading">
                        <i class="fas fa-building"></i>
                        시설별 변화 (상위 {{ result.byFacility.length }}개)
                    </h4>
                    <div class="detail-list">
                        <div
                            v-for="(row, i) in result.byFacility"
                            :key="i"
                            class="detail-item"
                            :class="getChangeStatusClass(row.delta)"
                        >
                            <div class="detail-item-name">{{ row.facilityName || row.facilityCode || "-" }}</div>
                            <div class="detail-item-bar">
                                <div class="detail-bar-track">
                                    <div
                                        class="detail-bar-fill"
                                        :style="getBarStyle(row.delta, maxAbsDelta(result.byFacility))"
                                    ></div>
                                </div>
                            </div>
                            <div class="detail-item-delta">
                                <span>{{ formatDeltaLocale(row.delta) }}</span>
                                <span class="detail-item-unit">kgCO2e</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="result.byCategory && result.byCategory.length" class="detail-section">
                    <h4 class="detail-heading">
                        <i class="fas fa-tags"></i>
                        카테고리별 변화 (상위 {{ result.byCategory.length }}개)
                    </h4>
                    <div class="detail-list">
                        <div
                            v-for="(row, i) in result.byCategory"
                            :key="i"
                            class="detail-item"
                            :class="getChangeStatusClass(row.delta)"
                        >
                            <div class="detail-item-name">
                                <span class="detail-scope-tag">S{{ row.scope }}</span>
                                {{ row.categoryCode || "-" }}
                            </div>
                            <div class="detail-item-bar">
                                <div class="detail-bar-track">
                                    <div
                                        class="detail-bar-fill"
                                        :style="getBarStyle(row.delta, maxAbsDelta(result.byCategory))"
                                    ></div>
                                </div>
                            </div>
                            <div class="detail-item-delta">
                                <span>{{ formatDeltaLocale(row.delta) }}</span>
                                <span class="detail-item-unit">kgCO2e</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import carbonApi from "../../../api";

export default {
    name: "InventoryCompareModal",
    props: {
        visible: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        result: { type: Object, default: null },
    },
    data() {
        return {
            boundaryNames: { org1: null, org2: null },
        };
    },
    watch: {
        result: {
            immediate: true,
            handler(val) {
                if (val && val.summary) this.loadBoundaryNames(val.summary);
                else this.boundaryNames = { org1: null, org2: null };
            },
        },
    },
    methods: {
        async loadBoundaryNames(summary) {
            this.boundaryNames = { org1: null, org2: null };

            const idx1 =
                summary.inventory1?.orgBoundaryIdx ?? summary.inventory1OrgBoundaryIdx ?? summary.orgBoundary1Idx;
            const idx2 =
                summary.inventory2?.orgBoundaryIdx ?? summary.inventory2OrgBoundaryIdx ?? summary.orgBoundary2Idx;

            try {
                if (idx1 != null) {
                    const r = await carbonApi.orgBoundary.getOrgBoundary(idx1);
                    // axios 응답: r.data = { success: true, data: { orgBoundaryName: ... } }
                    const data = r?.data?.data ?? r?.data;
                    const name = data?.orgBoundaryName;
                    if (name) {
                        this.$set(this.boundaryNames, "org1", name);
                    }
                }
                if (idx2 != null) {
                    const r = await carbonApi.orgBoundary.getOrgBoundary(idx2);
                    const data = r?.data?.data ?? r?.data;
                    const name = data?.orgBoundaryName;
                    if (name) {
                        this.$set(this.boundaryNames, "org2", name);
                    }
                }
            } catch (e) {
                console.error("[InventoryCompareModal] 조직경계 조회 실패:", e);
            }
        },
        getSummaryChangeClass(delta) {
            if (!delta || delta.status === "unchanged") return "change-unchanged";
            return delta.status === "increased" ? "change-increased" : "change-decreased";
        },
        getScopeClass(row) {
            return this.getSummaryChangeClass({ status: this.getChangeStatus(row.delta) });
        },
        getChangeStatus(delta) {
            if (delta == null) return "unchanged";
            const n = Number(delta);
            if (isNaN(n) || n === 0) return "unchanged";
            return n > 0 ? "increased" : "decreased";
        },
        getDeltaIcon(delta) {
            const status = delta?.status ?? this.getChangeStatus(delta?.absolute ?? delta);
            if (status === "increased") return "fas fa-arrow-up";
            if (status === "decreased") return "fas fa-arrow-down";
            return "fas fa-minus";
        },
        getChangeStatusClass(delta) {
            return this.getSummaryChangeClass({ status: this.getChangeStatus(delta) });
        },
        maxAbsDelta(rows) {
            if (!rows || !rows.length) return 1;
            let max = 0;
            rows.forEach((r) => {
                const a = Math.abs(Number(r.delta));
                if (!isNaN(a)) max = Math.max(max, a);
            });
            return max || 1;
        },
        getBarStyle(delta, maxAbs) {
            const n = Number(delta);
            if (isNaN(n) || maxAbs <= 0) return { width: "0%" };
            const pct = Math.min(100, (Math.abs(n) / maxAbs) * 100);
            return { width: pct + "%" };
        },
        formatNum(value) {
            if (value == null) return "-";
            const num = Number(value);
            if (isNaN(num)) return "-";
            return num.toLocaleString("ko-KR", { maximumFractionDigits: 1 });
        },
        formatDelta(value) {
            if (value == null || value === 0) return "0";
            const num = Number(value);
            if (isNaN(num)) return "-";
            const sign = num > 0 ? "+" : "";
            const abs = Math.abs(num);
            if (abs >= 1000000) return sign + (num / 1000000).toFixed(1) + "M";
            if (abs >= 1000) return sign + (num / 1000).toFixed(1) + "K";
            return sign + num.toFixed(1);
        },
        formatPercent(value) {
            if (value == null || value === 0) return "(0%)";
            const num = Number(value);
            if (isNaN(num)) return "(–)";
            const sign = num > 0 ? "+" : "";
            return "(" + sign + Math.abs(num).toFixed(1) + "%)";
        },
        formatDeltaLocale(value) {
            if (value == null) return "-";
            const num = Number(value);
            if (isNaN(num)) return "-";
            if (num === 0) return "0";
            const sign = num > 0 ? "+" : "";
            return sign + num.toLocaleString("ko-KR", { maximumFractionDigits: 1 });
        },
    },
};
</script>

<style scoped>
.compare-loading {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 24px;
    color: #64748b;
}

.compare-content {
    padding: 4px 0;
}

.compare-top-row {
    display: grid;
    grid-template-columns: 1.15fr 1fr 1fr 1fr;
    gap: 12px;
    align-items: stretch;
    margin-bottom: 20px;
}

.compare-top-row .scope-card {
    min-width: 0;
}

.main-card {
    border-width: 2px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.main-card .scope-delta-hero {
    margin-bottom: 8px;
}

.main-card .scope-delta-num {
    font-size: 20px;
}

.main-card .scope-delta-icon {
    font-size: 18px;
}

.main-card .scope-delta-percent {
    font-size: 12px;
}

.scope-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px 16px;
    transition:
        box-shadow 0.2s ease,
        border-color 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.scope-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
    border-color: #d1d5db;
}

.scope-card-label {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    margin-bottom: 10px;
}

.scope-delta-hero {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 8px;
    padding: 10px 0 4px;
    justify-content: flex-end;
}

.scope-delta-icon {
    font-size: 16px;
    line-height: 1;
}

.scope-delta-num {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.scope-delta-unit {
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
}

.scope-delta-percent {
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
    margin-left: 2px;
}

.scope-card.change-increased .scope-delta-icon,
.scope-card.change-increased .scope-delta-num {
    color: #dc2626;
}

.scope-card.change-increased .scope-delta-percent {
    color: #f87171;
}

.scope-card.change-decreased .scope-delta-icon,
.scope-card.change-decreased .scope-delta-num {
    color: #059669;
}

.scope-card.change-decreased .scope-delta-percent {
    color: #34d399;
}

.scope-card.change-unchanged .scope-delta-icon,
.scope-card.change-unchanged .scope-delta-num {
    color: #6b7280;
}

.scope-card.change-unchanged .scope-delta-percent {
    color: #9ca3af;
}

.scope-card-context {
    font-size: 12px;
    color: #9ca3af;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 12px;
    padding-top: 6px;
    border-top: 1px solid #f1f5f9;
    justify-content: space-evenly;
}

.scope-context-side {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    min-width: 0;
}

.scope-context-values-row {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
}

.scope-context-val {
    font-weight: 500;
    color: #64748b;
}

.scope-context-unit {
    font-size: 11px;
    color: #94a3b8;
}

.scope-context-boundary {
    font-size: 11px;
    color: #94a3b8;
}

.scope-context-divider {
    width: 1px;
    background: #f1f5f9;
    flex-shrink: 0;
    margin: 0 4px;
}

.detail-sections {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.detail-section {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 14px 16px;
}

.detail-heading {
    font-size: 13px;
    font-weight: 700;
    color: #475569;
    margin: 0 0 12px 0;
    display: flex;
    align-items: center;
    gap: 8px;
}

.detail-heading i {
    color: #94a3b8;
}

.detail-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.detail-item {
    display: grid;
    grid-template-columns: 1fr auto auto;
    align-items: center;
    gap: 10px 12px;
    padding: 6px 0;
    border-bottom: 1px solid #f1f5f9;
    font-size: 12px;
}

.detail-item:last-child {
    border-bottom: none;
}

.detail-item-name {
    color: #334155;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
}

.detail-scope-tag {
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 2px 5px;
    border-radius: 4px;
}

.detail-item-bar {
    width: 80px;
    flex-shrink: 0;
}

.detail-bar-track {
    height: 6px;
    background: #e2e8f0;
    border-radius: 3px;
    overflow: hidden;
}

.detail-bar-fill {
    height: 100%;
    border-radius: 3px;
    background: #94a3b8;
    transition: width 0.2s ease;
}

.detail-item.change-increased .detail-bar-fill {
    background: #dc2626;
}

.detail-item.change-decreased .detail-bar-fill {
    background: #059669;
}

.detail-item.change-unchanged .detail-bar-fill {
    background: #94a3b8;
}

.detail-item-delta {
    font-weight: 600;
    min-width: 72px;
    text-align: right;
    display: flex;
    align-items: baseline;
    justify-content: flex-end;
    gap: 4px;
}

.detail-item-unit {
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
}

.detail-item.change-increased .detail-item-delta > span:first-child {
    color: #dc2626;
}

.detail-item.change-decreased .detail-item-delta > span:first-child {
    color: #059669;
}

.detail-item.change-unchanged .detail-item-delta > span:first-child {
    color: #6b7280;
}
</style>
